import type { Event, Project, Session, SessionStatus } from "@mux/sdk/v2/client"
import type { SetStoreFunction, Store } from "solid-js/store"
import { getPinnedSessionIds } from "@/utils/pinned-sessions"
import {
  applyDirectoryEvent,
  applyGlobalEvent,
  applySessionEvent,
} from "./event-reducer"
import type { GlobalStore, State, VcsCache } from "./types"
import { hasSessionStatusPayload, parseSessionWaitingEvent, type PartDeltaEvent } from "./event-validation"

export type GlobalSyncEventHandlerInput = {
  globalStore: Store<GlobalStore>
  setGlobalStore: SetStoreFunction<GlobalStore>
  setProjects: (next: Project[] | ((draft: Project[]) => void)) => void
  refreshGlobal: () => void
  /** Loads the machine's global state if no snapshot has come from it yet; otherwise a no-op. */
  ensureGlobal: () => void
  pushDirectory: (directory: string) => void
  directories: () => string[]
  resyncDirectories: () => readonly string[]
  getDirectoryStore: (directory: string) =>
    | [Store<State>, SetStoreFunction<State>]
    | undefined
  markDirectory: (directory: string) => void
  getVcsCache: (directory: string) => VcsCache | undefined
  loadLsp: (directory: string, apply: (statuses: State["lsp"]) => void, signal?: AbortSignal) => void
  refreshSessionStatuses: () => Promise<unknown>
  /** Refetches the sidebar projection and every loaded chat list. */
  refreshSidebar: () => Promise<unknown>
  recoverSession?: (directory: string, sessionID: string, delta: PartDeltaEvent["properties"]) => void
  /** Called after a stream gap, when reset receipts for cached transcripts may have been missed. */
  reconcileTranscriptResets?: () => void
  acceptRequestEvent?: (event: Event) => boolean
  recoverySignal?: AbortSignal
  acceptSessionStatus: (sessionID: string, status: SessionStatus) => boolean
  recordSessionStatus: (sessionID: string, status: SessionStatus) => void
  /** Applies how many results a chat is still owed (the event's `launches`). */
  acceptSessionWaiting: (sessionID: string, launches: number) => void
  invalidateSession: (directory: string, sessionIDs: string[]) => void
  markSessionMissing: (directory: string, sessionID: string) => void
  serverKey: () => string
  preservePendingModelSelection: (session: Session) => Session
  onSessionEvent?: (directory: string, event: Event) => void
  /** Settings this machine serves changed: the account's, through its Mux Oracle, or a workspace's own. */
  onSettingsChanged?: () => void
  /** A starter's transcript changed, such as after a re-run on changed code. */
  onStartersChanged?: () => void
}

// A missing root list means every workspace may be stale.
function changedLspRoots(properties: unknown) {
  const roots = (properties as { roots?: unknown } | undefined)?.roots
  return Array.isArray(roots) ? roots.filter((root): root is string => typeof root === "string") : undefined
}

// Mirrors the server's /lsp filter: a workspace lists servers rooted inside its
// directory. A workspace whose directory is not loaded yet refreshes.
function lspRootsWithin(workspace: string | undefined, roots: string[]) {
  if (!workspace) return true
  const prefix = workspace.endsWith("/") ? workspace : workspace + "/"
  return roots.some((root) => root === workspace || root.startsWith(prefix))
}

export function createGlobalSyncEventHandler(input: GlobalSyncEventHandlerInput) {
  const refreshStatuses = () => void input.refreshSessionStatuses().catch(() => {})
  const refreshSidebar = () => void input.refreshSidebar().catch((error) => {
    if (!input.recoverySignal?.aborted) console.warn("Failed to refresh chat lists after an event stream gap", error)
  })
  const lspRequests = new Map<string, symbol>()
  const refreshLsp = (directory: string) => {
    const existing = input.getDirectoryStore(directory)
    if (!existing || input.recoverySignal?.aborted) return
    const request = Symbol()
    lspRequests.set(directory, request)
    input.loadLsp(directory, (statuses) => {
      if (lspRequests.get(directory) !== request) return
      lspRequests.delete(directory)
      if (input.recoverySignal?.aborted || input.getDirectoryStore(directory)?.[0] !== existing[0]) return
      existing[1]("lsp", statuses)
    }, input.recoverySignal)
  }

  // Chats read their folder through the workspace, so a relocated workspace
  // moves its cached chats and reloads its folder-derived state.
  const followWorkspaceFolder = (project: Project) => {
    const directory = project.directory
    if (!project.id || !directory) return
    const stale = (session: Session) => session.workspaceID === project.id && session.directory !== directory
    if (input.globalStore.session.some(stale)) input.setGlobalStore("session", stale, "directory", directory)
    const loaded = input.getDirectoryStore(project.id)?.[0].path?.worktree
    if (loaded && loaded !== directory) input.pushDirectory(project.id)
  }

  return (queued: { name: string; details: Event }) => {
    const directory = queued.name
    const event = queued.details

    if (directory === "global") {
      applyGlobalEvent({
        event,
        project: input.globalStore.project,
        setGlobalProject: input.setProjects,
      })
      if (event.type === "project.updated") {
        followWorkspaceFolder(event.properties as Project)
      } else if (event.type === "lsp.updated") {
        const roots = changedLspRoots(event.properties)
        for (const childDirectory of input.directories()) {
          if (!roots || lspRootsWithin(input.getDirectoryStore(childDirectory)?.[0].path?.worktree, roots)) refreshLsp(childDirectory)
        }
      } else if (event.type === "server.connected") {
        // A machine unreachable at startup answers now; its global state was never loaded.
        input.ensureGlobal()
        // Transcripts follow the chats the stream watches, whose gaps the
        // watch reports; this repairs what the workspace stream carries.
        const properties = event.properties as { replayed?: boolean; resumed?: boolean; resyncDirectories?: string[] } | undefined
        if (!properties?.replayed) {
          // Directories with replay coverage cannot have missed LSP updates.
          for (const childDirectory of properties?.resyncDirectories ?? input.directories()) refreshLsp(childDirectory)
          // A gap can hide chats created, archived, or snoozed elsewhere. The
          // sidebar refresh carries the status snapshot along with the lists.
          if (properties?.resumed) refreshStatuses()
          else refreshSidebar()
          // Summary events, including every reset receipt, stay continuous when
          // only workspace interest changed; only a real gap can miss a reset.
          if (!properties?.resumed) input.reconcileTranscriptResets?.()
        }
      } else if ((event as { type: string }).type === "settings.updated") {
        input.onSettingsChanged?.()
      } else if ((event as { type: string }).type === "starter.updated") {
        input.onStartersChanged?.()
      } else if ((event as { type: string }).type === "server.resync-required") {
        // The global refresh also reloads every open request, in any workspace.
        input.refreshGlobal()
        const resync = input.resyncDirectories()
        for (const childDirectory of resync) input.pushDirectory(childDirectory)
        refreshStatuses()
      }
      return
    }

    const directoryEvent =
      event.type === "vcs.branch.updated" ||
      event.type === "lsp.updated" ||
      event.type === "server.instance.disposed"
    if (directoryEvent) {
      const existing = input.getDirectoryStore(directory)
      if (!existing) return
      input.markDirectory(directory)
      const [store, setStore] = existing
      applyDirectoryEvent({
        event,
        directory,
        store,
        setStore,
        push: input.pushDirectory,
        vcsCache: input.getVcsCache(directory),
        loadLsp: () => refreshLsp(directory),
      })
      return
    }

    if (input.acceptRequestEvent?.(event) === false) return
    if ((event as { type: string }).type === "session.waiting") {
      const waiting = parseSessionWaitingEvent(event)
      if (waiting) input.acceptSessionWaiting(waiting.properties.sessionID, waiting.properties.launches)
      return
    }
    if (event.type === "session.status") {
      if (!hasSessionStatusPayload(event)) return
      const props = event.properties as { sessionID: string; status: SessionStatus }
      if (!input.acceptSessionStatus(props.sessionID, props.status)) return
      input.recordSessionStatus(props.sessionID, props.status)
    }
    const deletedSessionID =
      event.type === "session.deleted"
        ? (event.properties as { info?: { id?: string } })?.info?.id
        : undefined
    const reducedEvent = event.type === "session.updated"
      ? {
          ...event,
          properties: {
            ...(event.properties as object),
            info: input.preservePendingModelSelection((event.properties as { info: Session }).info),
          },
        } as Event
      : event
    applySessionEvent({
      event: reducedEvent,
      globalStore: input.globalStore,
      setGlobalStore: input.setGlobalStore,
      getDirectoryStore: input.getDirectoryStore,
      pinned: new Set(getPinnedSessionIds(input.serverKey())),
      onSessionCachesDropped: input.invalidateSession,
      onTextGap: (sessionID, delta) => input.recoverSession?.(directory, sessionID, delta),
    })
    input.onSessionEvent?.(directory, reducedEvent)
    if (deletedSessionID) input.markSessionMissing(directory, deletedSessionID)
  }
}
